import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { authService } from '../services/authService'
import { ROUTES, getAccountRoute } from '@shared/constants'
import Nav from '../components/Nav'
import { Users, Heart, Play, Video } from 'lucide-react'

// Helper to format large numbers
const formatNumber = (num: number) => {
    if (!num) return '0'
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k'
    return num.toString()
}

export default function AccountsPage() {
    const { user, accounts, accountsLoaded, setActiveAccount, logout } = useAuthStore()
    const navigate = useNavigate()
    
    // Check if user is admin (hi@lenkostudio.com or email containing 'admin')
    const isAdmin = user?.role === 'admin' || user?.email === 'hi@lenkostudio.com' || user?.email?.includes('admin')

    useEffect(() => {
        // Only redirect after accounts are loaded
        // If exactly 1 account and not admin, go directly to dashboard
        // Admins stay on this page to see/manage all accounts
        if (accountsLoaded && !isAdmin && accounts.length === 1) {
            setActiveAccount(accounts[0].id)
            navigate(getAccountRoute(accounts[0].username), { replace: true })
        }
    }, [accountsLoaded, accounts, navigate, isAdmin, setActiveAccount])

    const handleSelectAccount = (id: string) => {
        const account = accounts.find(acc => acc.id === id)
        if (account) {
            setActiveAccount(id)
            navigate(getAccountRoute(account.username))
        }
    }

    const handleConnect = () => {
        // Mock connection flow
        alert("This would open the TikTok OAuth flow.")
    }

    const handleLogout = async () => {
        try {
            await authService.logout()
            logout()
            navigate(ROUTES.LOGIN)
        } catch (error) {
            console.error('Logout failed:', error)
        }
    }

    // Rules:
    // 1. "hide the connect tiktok card for all admin users" -> showConnectCard = !isAdmin && ...
    // 2. "show all accounts listed on the card below" -> showSelectCard = isAdmin || ...
    
    const showConnectCard = !isAdmin && accounts.length === 0
    const showSelectCard = isAdmin || accounts.length === 0 || accounts.length > 1

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
            <Nav email={user?.email} onLogout={handleLogout} />
            <div className="max-w-4xl w-full space-y-8">
                <div className="text-center mb-12">
                     <h1 className="text-3xl font-bold text-white mb-2">
                        {accounts.length > 0 ? 'Select Account' : 'Welcome to Adam'}
                    </h1>
                    <p className="text-slate-400">
                        {accounts.length > 0 
                            ? 'Choose the TikTok account you want to manage.' 
                            : 'Connect your TikTok account to get started.'}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    
                    {/* Card 1: Connect TikTok Account */}
                    {showConnectCard && (
                        <div className={`bg-slate-900 border border-slate-800 rounded-2xl p-8 flex flex-col items-center text-center ${!showSelectCard ? 'md:col-span-2 max-w-md mx-auto' : ''}`}>
                            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-pink-500/20">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                     <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z"/>
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Connect TikTok</h3>
                            <p className="text-slate-400 text-sm mb-6">
                                Link your account to analyze your content, track growth, and get AI recommendations.
                            </p>
                            <button 
                                onClick={handleConnect}
                                className="w-full bg-white text-slate-950 font-bold py-3 px-6 rounded-xl hover:bg-slate-200 transition"
                            >
                                Connect Account
                            </button>
                        </div>
                    )}

                    {/* Card 2: Select Account Table */}
                    {showSelectCard && (
                        <div className={`bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden ${!showConnectCard ? 'md:col-span-2 max-w-2xl mx-auto w-full' : ''}`}>
                             <div className="p-6 border-b border-slate-800">
                                <h3 className="text-lg font-bold text-white">Your Accounts</h3>
                            </div>
                            
                            {accounts.length === 0 ? (
                                <div className="p-8 text-center text-slate-500 py-12">
                                    <p>No accounts connected yet.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 divide-y divide-slate-800">
                                    {accounts.map((account) => (
                                        <div 
                                            key={account.id} 
                                            onClick={() => handleSelectAccount(account.id)}
                                            className="group relative p-6 hover:bg-slate-800/50 transition cursor-pointer flex flex-col md:flex-row md:items-center gap-6"
                                        >
                                            {/* Account Identity */}
                                            <div className="flex items-center gap-4 min-w-[200px]">
                                                <div className="relative">
                                                    <img 
                                                        src={account.avatarUrl} 
                                                        alt={account.username} 
                                                        className="w-16 h-16 rounded-full bg-slate-800 object-cover border-2 border-slate-700 group-hover:border-primary-500 transition-colors"
                                                    />
                                                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center border-2 border-slate-900 ${
                                                        account.role === 'owner' ? 'bg-emerald-500' : 'bg-blue-500'
                                                    }`}>
                                                        {account.role === 'owner' ? (
                                                            <div className="text-[10px] font-bold text-white uppercase">O</div>
                                                        ) : (
                                                            <div className="text-[10px] font-bold text-white uppercase">M</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="font-bold text-white truncate text-lg">
                                                        {account.displayName || account.username}
                                                    </h4>
                                                    <p className="text-slate-400 text-sm font-medium">
                                                        @{account.username}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Main Stats */}
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                                                        <Users className="w-3.5 h-3.5" />
                                                        <span className="text-[10px] uppercase font-bold tracking-wider">Followers</span>
                                                    </div>
                                                    <span className="text-white font-bold">{formatNumber(account.stats?.followers || 0)}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                                                        <Heart className="w-3.5 h-3.5" />
                                                        <span className="text-[10px] uppercase font-bold tracking-wider">Likes</span>
                                                    </div>
                                                    <span className="text-white font-bold">{formatNumber(account.stats?.likes || 0)}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                                                        <Play className="w-3.5 h-3.5" />
                                                        <span className="text-[10px] uppercase font-bold tracking-wider">Views</span>
                                                    </div>
                                                    <span className="text-white font-bold">{formatNumber(account.stats?.views || 0)}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-1.5 text-slate-500 mb-0.5">
                                                        <Video className="w-3.5 h-3.5" />
                                                        <span className="text-[10px] uppercase font-bold tracking-wider">Videos</span>
                                                    </div>
                                                    <span className="text-white font-bold">{formatNumber(account.stats?.videos || 0)}</span>
                                                </div>
                                            </div>

                                            {/* Select Indicator */}
                                            <div className="hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pr-4">
                                                <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center text-primary-400">
                                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
